import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import WistiaPlayer from "../components/WistiaPlayer";
import AnimatedNumber from "../components/AnimatedNumber";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const SOCIAL_FORMATS = [
  {
    img: "/images/assets/7la9gmlo_2.jpg",
    label: "MVP della giornata",
    caption: "Card celebrativa per la migliore giocatrice di ogni weekend.",
    alt: "LVF TV — MVP Anna Danesi (Saugella Team Monza vs Chieri '76)",
  },
  {
    img: "/images/assets/6mqe5c3m_quiz-2-1.png",
    label: "Quiz interattivo",
    caption: "Domande a tema Serie A per testare il pubblico più appassionato.",
    alt: "LVF TV — Quiz: chi è la top scorer del campionato 2020/2021?",
  },
  {
    img: "/images/assets/e6ydv2kp_1-1.jpg",
    label: "Game Day announcement",
    caption: "Annuncio degli scontri principali con orari di trasmissione.",
    alt: "LVF TV — Game Day Banca Valsabbina Millenium Brescia vs Zanetti Bergamo",
  },
];

const WorkLvfTv = () => {
  const idx = WORKS.findIndex((w) => w.to === "/works/lvf-tv");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = idx >= 0 ? WORKS[(idx + 1) % WORKS.length] : WORKS[0];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "LVF TV", url: "/works/lvf-tv" },
  ]);

  return (
    <>
      <Seo
        title="LVF TV: 10.000 abbonati e +1.926% ROI | OnSports Case"
        description="Lancio LVF TV piattaforma streaming Serie A1 femminile volley: 10.000+ abbonati, +1.926% ROI Black Friday. OnSports performance per Lega Pallavolo."
        path="/works/lvf-tv"
        image="/images/assets/7la9gmlo_2.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/lvf-tv/hero.jpg"
        imageAlt="LVF TV — Lega Volley Femminile"
        eyebrow="● Case study · Business Impact · Performance Marketing · Subscriber Acquisition"
        titleLines={["Una stagione. Una piattaforma.", "DIECI MILA."]}
        highlightWord="DIECI MILA."
        subtitle="LVF TV era la piattaforma streaming proprietaria della Lega Volley Femminile — Serie A1 e Serie A2 femminile in diretta e on-demand. Ci hanno coinvolti con un obiettivo netto: raggiungere 10.000 abbonati entro l'inizio dei playoff. Lo abbiamo raggiunto attraverso un programma completo di performance marketing, sales page, campagne promozionali e social content — con ROI Black Friday del +1.926% e crescita degli utenti attivi del +120% in quattro mesi."
        meta="Cliente — Lega Volley Femminile  ·  Performance Marketing & Growth  ·  Obiettivo abbonati raggiunto"
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll><SectionEyebrow>Overview</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}><h2 className="display-h2 mt-6">Il progetto.</h2></RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}><p>LVF TV è stata per anni la piattaforma streaming proprietaria della Lega Volley Femminile, il dispositivo digitale che permetteva ai tifosi del volley italiano femminile di seguire tutte le partite di Serie A1 e Serie A2 in alta definizione, on-demand e in diretta, integrate da highlight, contenuti speciali e materiale editoriale prodotto dal team della Lega.</p></RevealOnScroll>
                <RevealOnScroll delay={0.2}><p>La sfida che la Lega ci ha portato era netta: aumentare in modo significativo il numero di abbonati alla piattaforma in una finestra temporale precisa — entro l'inizio dei playoff della stagione — passando da una base utenti già consolidata a oltre 10.000 abbonati attivi. Non era un esercizio di brand awareness né di consulenza strategica: serviva una macchina di acquisizione che funzionasse al meglio delle proprie possibilità, con ROI dimostrabile, in pochi mesi di lavoro.</p></RevealOnScroll>
                <RevealOnScroll delay={0.25}><p>Abbiamo gestito il programma end-to-end. Sviluppo della sales page di conversione, costruzione e gestione delle campagne advertising su Google e Meta, attivazione di campagne promozionali stagionali (Black Friday in primis), affiancamento consulenziale alla Lega sulla strategia di product e pricing, produzione e seeding di social formats originali per alimentare la curva di awareness. Il programma ha raggiunto l'obiettivo dei 10.000 abbonati nei tempi previsti, con metriche di ROI tra le più alte mai prodotte nei nostri progetti di subscriber acquisition sport.</p></RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">Scheda progetto</span>
                  <dl>
                    <SchedaRow label="Progetto">LVF TV — Subscriber Acquisition</SchedaRow>
                    <SchedaRow label="Cliente">Lega Volley Femminile</SchedaRow>
                    <SchedaRow label="Prodotto">Piattaforma streaming Serie A1 e A2 volley femminile</SchedaRow>
                    <SchedaRow label="Obiettivo">10.000+ abbonati entro l'inizio dei playoff</SchedaRow>
                    <SchedaRow label="Ruolo">Sales page · Google Ads · Meta Ads<br />Black Friday campaign<br />Affiancamento consulenziale<br />Social formats production</SchedaRow>
                    <SchedaRow label="Pilastro">Business Impact · Performance Marketing</SchedaRow>
                    <SchedaRow label="Canali">Web · Google · Meta · Social verticali</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>La sfida</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}><h2 className="display-h2 mt-6">Acquisire 10.000 abbonati in pochi mesi. Senza scorciatoie.</h2></RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}><p>Il volley femminile italiano ha una community appassionata ma di scala diversa rispetto al calcio o al basket — il bacino di potenziali abbonati a una piattaforma streaming dedicata è specifico, verticale, e va raggiunto con efficienza. Non si può sprecare budget su prospect non qualificati. Il CPA (costo per acquisizione) doveva restare entro soglie sostenibili rispetto al lifetime value medio di un abbonato della piattaforma, altrimenti la campagna non avrebbe avuto senso commerciale anche raggiungendo il numero.</p></RevealOnScroll>
              <RevealOnScroll delay={0.22}><p>A questo si aggiungeva una variabile narrativa: il pubblico del volley femminile italiano si è dilatato significativamente negli ultimi anni — sull'onda dei successi della nazionale, della pallavolo che è entrata nelle case con i Mondiali e le Olimpiadi — e include sia tifosi storici sia nuovi appassionati che si avvicinano allo sport per la prima volta. Una macchina di acquisizione doveva parlare contemporaneamente a queste due audience, senza diluire il messaggio. Abbiamo costruito le campagne con segmentazioni dedicate, creative differenziate per stadio di consapevolezza, sales page ottimizzata per il punto di decisione.</p></RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — APPROCCIO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Approccio</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}><h2 className="display-h2 mt-6">Quattro leve. Un solo funnel.</h2></RevealOnScroll>
              <RevealOnScroll delay={0.18}><p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">Abbiamo costruito il programma come un funnel completo, in cui ogni leva è stata progettata per alimentare la successiva. Niente attività isolate, niente esperimenti scollegati. Le quattro filiere hanno lavorato sotto un'unica regia, con KPI condivisi e revisione settimanale dei dati.</p></RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}><ApproccioBlock num="01" title="Sales page & product" body="Sviluppo della sales page di conversione progettata per la decisione di abbonamento: architettura del messaggio costruita sui benefit chiave (tutte le partite live, alta definizione, highlight, contenuti speciali), social proof dai tifosi esistenti, sezione FAQ per abbattere le obiezioni più frequenti. Ottimizzazione mobile-first del checkout di sottoscrizione." /></RevealOnScroll>
            <RevealOnScroll delay={0.12}><ApproccioBlock num="02" title="Google & Meta advertising" body="Strutturazione delle campagne paid su Google Ads (Search per keyword di intenzione e Performance Max) e Meta Ads (Facebook + Instagram, prospecting e retargeting). Audience tarate sul pubblico volley italiano sia consolidato sia in fase di consapevolezza. Creative ad alta produzione, costantemente A/B testate per CPA e tasso di conversione." /></RevealOnScroll>
            <RevealOnScroll delay={0.19}><ApproccioBlock num="03" title="Black Friday & campaign activation" body="Pianificazione e attivazione delle finestre commerciali stagionali — Black Friday in primis, ma anche le altre occasioni promozionali coerenti col calendario sportivo. La sola campagna Black Friday ha generato un ROI superiore al +1.926% sul budget paid investito." /></RevealOnScroll>
            <RevealOnScroll delay={0.26}><ApproccioBlock num="04" title="Social formats & content" body="Produzione di una serie di social formats originali per i canali della Lega e di LVF TV — card editoriali, quiz interattivi, game day announcement, MVP della giornata. Contenuti pensati per aumentare engagement della community e creare touchpoint costanti tra abbonati e prospect nelle settimane di campagna." /></RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — IMPATTO · VIDEO QUADRATO + KPI ANIMATI */}
      <section
        className="relative section-pad bg-dodgers text-galactico overflow-hidden"
        data-testid="section-impatto"
      >
        {/* Pattern LVF TV decorativo */}
        <div
          className="absolute inset-0 opacity-15 pointer-events-none"
          style={{
            backgroundImage:
              "url('/images/casi-studio/lvf-tv/lvf-tv-pattern.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          aria-hidden="true"
          data-testid="impatto-pattern"
        />

        <div className="relative z-10 container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Impatto</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}><h2 className="display-h2 mt-6">I numeri parlano. Forte.</h2></RevealOnScroll>
              <RevealOnScroll delay={0.18}><p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[720px] opacity-90">Subscriber acquisition con ROI tracciabile dal primo giorno. Tutti i numeri qui sotto sono dati reali di campagna — paid media, sales page conversion, attivazione promozionale — verificati dalla Lega Volley Femminile a fine ciclo.</p></RevealOnScroll>
            </div>
          </div>

          {/* KPI principale full-width */}
          <RevealOnScroll delay={0.05}>
            <div className="text-center mb-16 md:mb-24" data-testid="kpi-main">
              <AnimatedNumber
                end={10000}
                suffix="+"
                duration={2400}
                className="font-display text-lime block leading-[0.9] text-[160px] md:text-[240px] tracking-tight"
              />
              <p className="font-condensed uppercase tracking-widest text-base md:text-xl mt-4 md:mt-6">
                Abbonati raggiunti entro l'inizio dei playoff
              </p>
            </div>
          </RevealOnScroll>

          {/* Split: video quadrato + 3 KPI animati */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
            <RevealOnScroll>
              <div className="max-w-[500px] mx-auto w-full">
                <div className="overflow-hidden shadow-2xl" data-testid="video-impatto">
                  <WistiaPlayer mediaId="b1yz7vrxq4" aspect={1.0} autoplay loop muted />
                </div>
              </div>
            </RevealOnScroll>
            <div className="space-y-12 md:space-y-14">
              <RevealOnScroll delay={0.1}>
                <div>
                  <AnimatedNumber
                    end={1926.86}
                    decimals={2}
                    prefix="+"
                    suffix="%"
                    className="font-display text-lime block leading-[0.9] text-[100px] md:text-[140px] tracking-tight"
                  />
                  <p className="text-galactico/85 text-base md:text-lg mt-3 max-w-sm">
                    di TOTAL ROI sulla campagna Black Friday
                  </p>
                </div>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <div>
                  <AnimatedNumber
                    end={916}
                    prefix="+"
                    suffix="%"
                    className="font-display text-lime block leading-[0.9] text-[100px] md:text-[140px] tracking-tight"
                  />
                  <p className="text-galactico/85 text-base md:text-lg mt-3 max-w-sm">
                    di ROI sugli already active users dai paid media
                  </p>
                </div>
              </RevealOnScroll>
              <RevealOnScroll delay={0.26}>
                <div>
                  <AnimatedNumber
                    end={120}
                    prefix="+"
                    suffix="%"
                    className="font-display text-lime block leading-[0.9] text-[100px] md:text-[140px] tracking-tight"
                  />
                  <p className="text-galactico/85 text-base md:text-lg mt-3 max-w-sm">
                    di utenti attivi sulla piattaforma in 4 mesi
                  </p>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 6 — SOCIAL FORMATS */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-social-formats">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Social formats</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}><h2 className="display-h2 mt-6">Una serie di formati nativi per la community.</h2></RevealOnScroll>
              <RevealOnScroll delay={0.18}><p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px] opacity-90">Oltre alla performance pura, abbiamo prodotto per LVF TV una serie di format editoriali social progettati per aumentare il tempo di attenzione della community attorno alla piattaforma — non solo nei momenti di acquisizione ma in tutta la settimana del matchday. Carosello MVP della giornata, quiz interattivi sulla Serie A, announcement game day con scontri di cartello. Sono asset replicabili che la Lega ha poi continuato ad usare anche dopo la chiusura del nostro contratto.</p></RevealOnScroll>
            </div>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12"
            data-testid="social-formats-grid"
          >
            {SOCIAL_FORMATS.map((f, i) => (
              <RevealOnScroll key={i} delay={i * 0.06}>
                <div className="flex flex-col" data-testid={`social-format-${i}`}>
                  <div className="aspect-[9/16] rounded-md overflow-hidden shadow-xl bg-dodgers/[0.04]">
                    {f.img ? (
                      <img
                        src={f.img}
                        alt={f.alt}
                        loading="lazy"
                        className="block w-full h-full object-cover"
                        draggable="false"
                      />
                    ) : (
                      <div
                        className="w-full h-full bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center"
                        data-testid={`social-format-placeholder-${i}`}
                      >
                        <span
                          className="font-condensed uppercase tracking-widest text-galactico"
                          style={{ fontSize: "12px" }}
                        >
                          Format in arrivo
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="block mt-3 font-condensed uppercase tracking-widest text-lime text-[11px]">
                    ● {f.label}
                  </span>
                  <p className="mt-1 text-dodgers/80 text-sm leading-[1.55]">
                    {f.caption}
                  </p>
                </div>
              </RevealOnScroll>
            ))}

            {/* Slot 4 · Video Wistia verticale 9:16 — integrato nella griglia */}
            <RevealOnScroll delay={SOCIAL_FORMATS.length * 0.06}>
              <div className="flex flex-col" data-testid="social-format-video">
                <div className="aspect-[9/16] rounded-md overflow-hidden shadow-xl bg-dodgers/[0.04]">
                  <WistiaPlayer mediaId="o52r39ua9o" aspect={0.5625} />
                </div>
                <span className="block mt-3 font-condensed uppercase tracking-widest text-lime text-[11px]">
                  ● Social format in movimento
                </span>
                <p className="mt-1 text-dodgers/80 text-sm leading-[1.55]">
                  Esempio video del format social — versione in motion.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — LA CODA DEL PROGETTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-coda">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Un outcome oltre i numeri</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}><h2 className="display-h2 mt-6">Il progetto che ci ha portato Volleyball World come cliente.</h2></RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}><p>Negli anni successivi alla fine del nostro contratto con LVF TV, la Lega Volley Femminile ha sottoscritto un accordo strategico con Volleyball World — la società di proprietà della FIVB che gestisce a livello globale i diritti commerciali e media del volley — per la migrazione dei suoi contenuti streaming sotto la piattaforma globale del marchio internazionale. LVF TV come prodotto è stata sostituita.</p></RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  Quel passaggio ha avuto una conseguenza diretta per noi: il
                  lavoro svolto su LVF TV è diventato la prova tangibile delle
                  nostre capacità nel volley italiano, e Volleyball World ci ha
                  contattati per costruire la propria presenza editoriale
                  italiana — il progetto{" "}
                  <Link
                    to="/works/volleyball-world-italia"
                    className="text-lime underline underline-offset-4 hover:opacity-80"
                    data-testid="link-volleyball-world"
                  >
                    Volleyball World Italia
                  </Link>{" "}
                  che oggi è uno dei nostri casi di riferimento. Un caso
                  commerciale che ha generato un secondo cliente. È il valore
                  non lineare del lavoro fatto bene.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 8 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll><SectionEyebrow>Credits</SectionEyebrow></RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Cliente">Lega Volley Femminile</SchedaRow>
                <SchedaRow label="Progetto">LVF TV — Subscriber Acquisition Program</SchedaRow>
                <SchedaRow label="Prodotto">Piattaforma streaming Serie A1 &amp; A2 volley femminile</SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Sales page · Google Ads · Meta Ads<br />
                  Black Friday &amp; seasonal campaigns<br />
                  Affiancamento consulenziale<br />
                  Social formats production
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Performance Marketing &amp; Growth<br />
                  Content &amp; IP Studio
                </SchedaRow>
                <SchedaRow label="Outcome">
                  10.000+ abbonati raggiunti · ROI BF +1.926%<br />
                  +120% utenti attivi in 4 mesi
                </SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — CTA FINALE */}
      <section className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10" data-testid="section-cta-finale">
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[30ch]">
              Hai una piattaforma da popolare o un prodotto digitale da scalare?
              Costruiamo insieme il suo <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Performance marketing puro con ROI tracciabile dal primo mese.
              Sales page, Google Ads, Meta Ads, campagne promozionali
              stagionali. Tutto sotto un'unica regia, KPI condivisi prima del
              lancio. Lo abbiamo fatto per LVF TV — possiamo farlo per te.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone"><Button variant="primary">Parliamone</Button></Link>
              <Link to="/works" data-testid="cta-altri-casi"><Button variant="secondary-light">Vedi altri casi studio</Button></Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll><SectionEyebrow>Naviga i progetti</SectionEyebrow></RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to={prev.to || "/works"} className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4" data-hover="true" data-testid="nav-prev">
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">← Progetto precedente</span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">{prev.title}</span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link to={next.to || "/works"} className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end" data-hover="true" data-testid="nav-next">
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">Progetto successivo <ArrowUpRight size={14} /></span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">{next.title}</span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkLvfTv;
